import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Slider,
  TextField,
  Select,
  MenuItem,
  Box,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { countries, findCountryOption } from 'utils/countriesWithCodes';
import ReplayIcon from '@mui/icons-material/Replay';
import LockIcon from '@mui/icons-material/Lock';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalContext } from 'providers/GlobalProvider';
import DatahubAPI from 'api/DatahubAPI';
import { CircularProgress } from '@mui/material';
import {
  getUnsupportedHtsChapter,
  unsupportedHtsChapterMessage,
} from 'utils/htsChapters';

interface ITariffSearchProps {
  inputs: any;
  setInputs: any;
  handleSubmit: any;
  userHasSearch: boolean;
  effectDate: Date | null;
  setEffectDate: any;
  loadDate: Date | null;
  setLoadDate: any;
  results: boolean;
  setModalOpen: any;
  productValue: number;
  setProductValue: any;
  lineSplitShow: boolean;
  loadingCountryShow: boolean;
  ammountShow: boolean;
  tariffResults: any;
  loadingCountry: string;
  setLoadingCountry: any;
  countryFieldKey?: string;
  countryFieldCodes: string[];
  amountInputs: Record<string, number>;
  setAmountInputs: any;
  materialPctByKey: Record<string, number>;
  setMaterialPctByKey: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  clearPage: any;
  setShowDownload: any;
  readOnly?: boolean;
  isBulkMode?: boolean;
  // Which schedule the autocomplete + lookup should query. Default 'US'
  // keeps the legacy behavior for callers that don't pass it.
  tariffCountry?: 'US' | 'MX' | 'CA';
}

const metricUnits = ['mg', 'g', 'kg', 't', 'mL', 'L', 'mm', 'cm', 'm'];

const inputFieldSx = {
  background: 'var(--Background-200, var(--input-color))',
  borderRadius: '8px',
  'min-width': '87px',
  width: 'fit-content',
  maxWidth: 150,
  '& .MuiInputBase-root': {
    width: 'fit-content',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    border: '1px solid var(--secondary-color)',
    height: '45px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    color: 'var(--input-text-color)',
    'padding-right': '10px',
  },
  '& .MuiInputBase-input': {
    color: 'var(--Text-Main, var(--input-text-color))',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    textOverflow: 'ellipsis',
  },
  '& .MuiSvgIcon-root': { color: 'var(--input-text-color)' },
};

// Canonical outline styling shared by every "simple" field on the Tariff
// Checker form -- HTS Code, Origin Country, Transportation Method, Entry
// Date, Loading Date, and Country of Smelt/Pour. Before this, each field
// carried its own near-duplicate `sx` block and they had quietly drifted:
// Transportation Method and both DatePickers used a different hover border
// color (`--input-placeholder-color`) than HTS Code / Origin Country /
// Country of Smelt (`--secondary-color`), and HTS Code alone used
// `minHeight` instead of a fixed `height`. Spread this into each field's
// `sx` and layer on only what that field actually needs beyond it (e.g. a
// placeholder-opacity tweak, or HTS Code's clear-indicator color).
//
// Product Value is a plain <input>, not a MUI field, so it can't consume a
// JS style object -- it gets the equivalent `.tariff-value-input` CSS class
// in resources/styles/index.css instead, same visual spec.
const tariffFieldSx = {
  background: 'var(--Background-200, var(--input-color))',
  borderRadius: '8px',
  height: '45px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '45px',
    color: 'var(--input-text-color)',
    '&.Mui-focused': {
      color: 'var(--focused-text-color)',
    },
  },
  // Restyle MUI's actual border element -- the .MuiOutlinedInput-notchedOutline
  // <fieldset> every outlined field already renders -- instead of ALSO putting
  // a `border` directly on .MuiOutlinedInput-root like this used to. Doing
  // both drew two overlapping borders (our custom-color root border sitting
  // on top of MUI's own default-gray fieldset border underneath it), which is
  // why these fields never quite matched Product Value's single plain-CSS
  // border even once both used the same color variable.
  //
  // Hover/focus deliberately use a DIFFERENT color than rest
  // (--input-placeholder-color vs --secondary-color) -- setting them to the
  // same value, as this block used to, makes hover a no-op with nothing to
  // see. This is also what Product Value's matching :hover rule in
  // resources/styles/index.css (.tariff-value-input) mirrors.
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--input-placeholder-color)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--input-placeholder-color)',
  },
  '& .MuiInputBase-input, & .MuiAutocomplete-input': {
    color: 'var(--Text-Main, var(--input-text-color))',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--focused-text-color)',
  },
};

const sliderSx = {
  px: 1,
  width: '100%',
  marginRight: '5px',
  marginLeft: '5px',
  '& .MuiSlider-rail': { background: 'var(--secondary-color)', opacity: 1 },
  '& .MuiSlider-track': { border: 'none' },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    '&::before': { boxShadow: 'none' },
  },
};

const COUNTRY_LABELS: Record<string, string> = {
  SMELTCOUNTRY: 'Country of Smelt',
  POURCOUNTRY: 'Country of Pour',
  LOADINGCOUNTRY: 'Loading Country',
};

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .replace(/[_\-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

// An archived schedule record carries the marker on the HS Code itself
// ("8708.29.51.10 - Archived"). Both the US and foreign datahub lookups match
// HS Code by *prefix* precisely so those records come back — the HTS Code
// History panel is built from them — so they cannot be filtered server-side
// without breaking that. They must never be offered as a classification to
// pick, so drop them here. Mirrors the screening query's must_not wildcard on
// 'HS Code.keyword'.
const isArchivedHtsRecord = (hsCode: unknown): boolean =>
  /\barchived\b/i.test(String(hsCode ?? ''));

const labelFromKey = (key?: string, fallback = 'Loading Country') => {
  if (!key) {
    return fallback;
  }
  const k = key.toUpperCase();
  return COUNTRY_LABELS[k] ?? titleCase(k);
};

const TariffRagSearch: React.FC<ITariffSearchProps> = ({
  inputs,
  setInputs,
  handleSubmit,
  effectDate,
  setEffectDate,
  loadDate,
  setLoadDate,
  results,
  setModalOpen,
  productValue,
  setProductValue,
  lineSplitShow,
  loadingCountryShow,
  ammountShow,
  tariffResults,
  loadingCountry,
  setLoadingCountry,
  countryFieldKey,
  countryFieldCodes,
  amountInputs,
  setAmountInputs,
  materialPctByKey,
  setMaterialPctByKey,
  clearPage,
  setShowDownload,
  readOnly = false,
  isBulkMode,
  userHasSearch,
  tariffCountry = 'US',
}) => {
  const { isWhiteLabel, tenant_access } = useContext(GlobalContext);
  const history = useHistory();
  const [showAnalyzerBanner, setShowAnalyzerBanner] = React.useState(true);
  const isInteractionDisabled = !userHasSearch || readOnly;
  const emailTo =
    isWhiteLabel && tenant_access?.config?.emailTo
      ? tenant_access.config.emailTo
      : 'info@bitedata.io';
  const DISCLAIMER_TEXT = (
    <>
      Disclaimer: Users are responsible for manually verifying product
      scope, effective dates, list numbers, applicable exemptions, and
      FTAs - the outputs of this tool are an unofficial opinion of the
      BITE Data Corporation. It is intended to assist in identifying
      potential tariff implications and should be used alongside a
      comprehensive compliance review. Reliance upon this advice does not
      constitute reasonable care. In order to exercise the highest degree
      of reasonable care, BITE Data Corporation recommends that users
      perform a complete review, which may include application for a
      binding ruling from the relevant customs authority. Should you need
      further assistance in this
      matter, please contact us at{' '}
      <a href={`mailto:${emailTo}`}>{emailTo}</a>, where we can direct you
      to additional resources for your trade compliance needs.
      <br />
      <br />
      View recent and upcoming data updates{' '}
      <a
        href='https://bitedata.notion.site/'
        target='_blank'
        rel='noopener noreferrer'
      >
        here
      </a>
    </>
  );
  const disclaimerText =
  isWhiteLabel && tenant_access?.config?.disclaimerText ? (
    <div
      dangerouslySetInnerHTML={{
        __html: tenant_access.config.disclaimerText,
      }}
    />
  ) : (
    DISCLAIMER_TEXT
  );

  const [htsOptions, setHtsOptions] = React.useState<any[]>([]);
  const [isHtsLoading, setIsHtsLoading] = React.useState(false);
  // Refs rather than state: both only gate fetches, and they're read and
  // written inside the same keystroke handler where a state value would still
  // be the previous render's stale closure.
  const fetchedPrefixRef = React.useRef<string>('');
  const htsRequestIdRef = React.useRef(0);
  const [htsInputValue, setHtsInputValue] = React.useState<string>(
    inputs.htsCode || '',
  );
  // Chapter 98/99 codes can't be run (see utils/htsChapters). Flagged under
  // the input and Run Check is disabled; TariffPage refuses them too.
  const unsupportedHtsChapter = getUnsupportedHtsChapter(inputs.htsCode);

  // Wipe the suggestion list. Bumping the request id is the important part:
  // every reset path can fire while a prefix fetch is still in flight, and that
  // response only checks the id before writing — so without this it lands after
  // the reset and repopulates the dropdown with the previous country's (or the
  // previous code's) options. The id also has to move for isHtsLoading, since
  // the superseded request's `finally` now skips clearing it.
  const resetHtsOptions = React.useCallback(() => {
    htsRequestIdRef.current += 1;
    setIsHtsLoading(false);
    setHtsOptions([]);
    fetchedPrefixRef.current = '';
  }, []);

  const formatHtsPretty = (raw: string) => {
    const d = raw.replace(/\D/g, '');
    const parts = [
      d.slice(0, 4),
      d.slice(4, 6),
      d.slice(6, 8),
      d.slice(8, 10),
    ].filter(Boolean);
    return parts.join('.');
  };

  const filterHtsOptions = createFilterOptions<any>({
    matchFrom: 'any',
    stringify: (option: any) => option.code + option.label, // Allows searching by raw digits or dots
  });

  React.useEffect(() => {
    if (loadingCountryShow && !loadingCountry && inputs?.originCountry) {
      setLoadingCountry(inputs.originCountry);
    }
  }, [loadingCountryShow]);

  // Clear cached HTS suggestions when the user switches between US / MX /
  // CA tabs so the dropdown doesn't show stale options from the previous
  // schedule before the next prefix fetch completes.
  React.useEffect(() => {
    resetHtsOptions();
  }, [tariffCountry, resetHtsOptions]);

  const isDollarFocused = React.useRef(false);

  const initialMaterialPct = Number.isFinite(inputs.materialPct)
    ? inputs.materialPct
    : 0;

  const [materialDollarInput, setMaterialDollarInput] = React.useState(
    productValue && productValue > 0
      ? ((productValue * initialMaterialPct) / 100).toFixed(2)
      : '',
  );

  const [multiDollarInputs, setMultiDollarInputs] = React.useState<
    Record<string, string>
  >({});
  const multiDollarFocus = React.useRef<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!productValue || productValue <= 0) {
      setMultiDollarInputs({});
      return;
    }
    setMultiDollarInputs((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(materialPctByKey || {})) {
        if (!multiDollarFocus.current[key]) {
          const amt = ((productValue * materialPctByKey[key]) / 100).toFixed(2);
          if (next[key] !== amt) {
            next[key] = amt;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [productValue, materialPctByKey]);

  React.useEffect(() => {
    // ADD THIS CHECK: Don't overwrite the input if the user is typing in it
    if (isDollarFocused.current) {
      return;
    }

    if (
      productValue &&
      productValue > 0 &&
      Number.isFinite(inputs.materialPct)
    ) {
      const amt = (productValue * inputs.materialPct) / 100;
      setMaterialDollarInput(amt.toFixed(2));
    } else {
      setMaterialDollarInput('');
    }
  }, [productValue, inputs.materialPct]);

  const clamp01pct = (n: number) => {
    const num = Number(n);
    if (!Number.isFinite(num)) {
      return 0;
    }
    const clamped = Math.max(0, Math.min(100, num));
    return Math.round(clamped * 100) / 100;
  };

  const boundedPctUpdate = (
    prev: Record<string, number>,
    key: string,
    rawNext: number,
    orderKeys: string[],
  ) => {
    const next = { ...prev };
    next[key] = clamp01pct(rawNext);

    const keys = (
      orderKeys && orderKeys.length ? orderKeys : Object.keys(next)
    ).filter((k) => k in next);
    const sum = keys.reduce((s, k) => s + (Number(next[k]) || 0), 0);
    let overflow = Math.max(0, sum - 100);

    for (const k of keys) {
      if (overflow <= 0) {
        break;
      }
      if (k === key) {
        continue;
      }
      const cur = Number(next[k]) || 0;
      if (cur <= 0) {
        continue;
      }
      const take = Math.min(cur, overflow);
      next[k] = clamp01pct(cur - take);
      overflow = overflow - take;
    }

    if (overflow > 0) {
      next[key] = clamp01pct((Number(next[key]) || 0) - overflow);
    }

    return next;
  };

  const parseCurrencyToNumber = (raw: string): number => {
    let s = raw.replace(/[^0-9.]/g, '');
    const parts = s.split('.');
    if (parts.length > 2) {
      s = `${parts[0]}.${parts[1]}`;
    }
    if (parts[1] && parts[1].length > 2) {
      s = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    if (s === '' || s === '.') {
      return 0;
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const getEstimatedDutyDisplay = (rateRaw: string, unit: string, qty: any) => {
    if (!rateRaw) return '';

    // 1. Escape the unit string for regex safety (e.g. "m2" -> "m2")
    const safeUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // 2. Regex to find the specific rate for this unit
    // Matches: (Number) (Currency) ... / (Unit)
    // Example: "3¢/kg" -> matches "3" and "¢"
    const regex = new RegExp(
      `([\\d\\.]+)\\s*(¢|c|cents?|\\$|usd)\\s*(?:\\/|per)\\s*${safeUnit}`,
      'i',
    );

    const match = rateRaw.match(regex);

    if (match) {
      let val = parseFloat(match[1]);
      const currency = match[2].toLowerCase();

      // Convert cents to dollars
      if (['¢', 'c', 'cent', 'cents'].includes(currency)) {
        val = val / 100;
      }

      // Calculate Total: Rate * Quantity
      const numericQty = Number(qty) || 0;
      const total = val * numericQty;

      return `$${total.toFixed(2)}`;
    }

    // Fallback: If we can't parse it (e.g. complex string), show the original text
    return rateRaw;
  };

  type COpt =
    | string
    | {
        name?: string;
        label?: string;
        countryName?: string;
        iso2?: string;
        alpha2?: string;
        alpha2Code?: string;
        usCustomsCountryCode?: string;
        country?: {
          name?: string;
          countryName?: string;
          iso2?: string;
          alpha2?: string;
          alpha2Code?: string;
        };
      };

  const getName = (o: COpt) => (typeof o === 'string' ? o : o?.name ?? '');
  const getIso = (o: COpt) =>
    typeof o === 'string' ? '' : o?.iso2 ?? ''?.toUpperCase();
  const displayLabel = (o: COpt) => {
    const n = getName(o);
    const code = getIso(o);
    return code ? `${n} - ${code}` : n;
  };

  const UNIT_LABELS: Record<string, string> = {
    'line/gross': 'Gross Lines',
    pcs: 'Pieces',
    prs: 'Pairs',
    m2: 'm²',
    cm2: 'cm²',
    m3: 'm³',
    cm3: 'cm³',
    kw: 'kW',
    kwh: 'kWh',
  };
  const prettyUnit = (u: string) => UNIT_LABELS[u] ?? u;

  const loadingCountryLabel = labelFromKey(countryFieldKey, 'Loading Country');
  const codeSuffix =
    Array.isArray(countryFieldCodes) && countryFieldCodes.length
      ? ` - For ${countryFieldCodes.join(', ')}`
      : '';
  const loadingCountryFinalLabel = `${loadingCountryLabel}${codeSuffix}`;

  const toDateOnly = (d: Date | null): Date | null => {
    if (!d) {
      return null;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  const isAfter = (a: Date | null, b: Date | null): boolean =>
    Boolean(a && b && a.getTime() > b.getTime());
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  React.useEffect(() => {
    const ld = toDateOnly(loadDate);
    const ed = toDateOnly(effectDate);
    if (isAfter(ld, ed)) {
      setLoadDate(ed);
    }
  }, [effectDate, loadDate]);

  // FIX: Use pointerEvents to disable interaction without changing visual style
  // Do NOT add backgroundColor or opacity changes here to keep it looking "live"
  const disabledSx = readOnly
    ? {
        pointerEvents: 'none' as const,
      }
    : {};

  const disabledSelectorSx = readOnly
    ? {
        pointerEvents: 'none' as const,
      }
    : {};

  return (
    <div className='screening-search-container-full'>
      <div className='screening-inputs-list'>
        <div className='screening-input-row'>
          <div className='screening-label-input'>
            <div style={{ width: '-webkit-fill-available' }}>
              <div className='screening-input-group' style={{ marginTop: 0 }}>
                <div className='screening-input-minors'>
                  {/* HTS Code */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>HTS Code</label>
                    <Autocomplete
                      freeSolo
                      options={htsOptions}
                      loading={isHtsLoading}
                      readOnly={readOnly}
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      // Format the selected code with dots inside the input box
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : formatHtsPretty(option.code)
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                          {option.label}
                        </li>
                      )}
                      filterOptions={filterHtsOptions}
                      value={
                        inputs.htsCode
                          ? htsOptions.find(
                              (o) =>
                                o.code === inputs.htsCode.replace(/\D/g, ''),
                            ) || {
                              code: inputs.htsCode.replace(/\D/g, ''),
                              label: htsInputValue || inputs.htsCode,
                            }
                          : null
                      }
                      isOptionEqualToValue={(option, val) =>
                        option.code === val.code
                      }
                      inputValue={htsInputValue}
                      onInputChange={async (event, newInputValue, reason) => {
                        // 1. Fully wipe everything on clear
                        if (reason === 'clear') {
                          setHtsInputValue('');
                          setInputs({ ...inputs, htsCode: '' });
                          resetHtsOptions();
                          return;
                        }

                        setHtsInputValue(newInputValue);

                        // 2. THIS FIXES THE RESET BUG:
                        // Keep the actual form state in sync with backspaces/typing
                        if (reason === 'input') {
                          setInputs({
                            ...inputs,
                            htsCode: newInputValue.replace(/\D/g, ''),
                          });
                        }

                        const digits = newInputValue.replace(/\D/g, '');

                        // Don't offer chapter 98/99 codes to pick.
                        if (getUnsupportedHtsChapter(digits)) {
                          resetHtsOptions();
                          return;
                        }

                        if (digits.length >= 6) {
                          // Re-fetch at the 6/8/10-digit boundaries instead of
                          // freezing on the first 6. A single 6-digit basket
                          // (e.g. 3004.90) can hold more codes than the
                          // backend's result window returns, so its deeper
                          // 10-digit lines are only reachable once the prefix
                          // narrows as the user keeps typing.
                          const prefixLength =
                            digits.length >= 10
                              ? 10
                              : digits.length >= 8
                              ? 8
                              : 6;
                          const prefix = digits.slice(0, prefixLength);
                          // Re-key the cached prefix by country so switching
                          // tabs always re-fetches from the right schedule
                          // instead of showing stale US options on the MX/CA
                          // tab (or vice versa).
                          const prefixKey = `${tariffCountry}:${prefix}`;

                          if (prefixKey !== fetchedPrefixRef.current) {
                            fetchedPrefixRef.current = prefixKey;
                            // Typing a full code now fires up to three
                            // overlapping fetches, so only the newest may write
                            // state — otherwise a slow 6-digit response lands
                            // after the 10-digit one and clobbers it.
                            const requestId = htsRequestIdRef.current + 1;
                            htsRequestIdRef.current = requestId;
                            setIsHtsLoading(true);
                            try {
                              const formattedPrefix = formatHtsPretty(prefix);

                              const res =
                                tariffCountry === 'US'
                                  ? await DatahubAPI.retrieveHTSCode({
                                      code: formattedPrefix,
                                      country: inputs.originCountry || '',
                                    })
                                  : await DatahubAPI.retrieveForeignHTSCode({
                                      code: formattedPrefix,
                                      sourceCountry:
                                        tariffCountry === 'MX'
                                          ? 'Mexico'
                                          : 'Canada',
                                    });

                              if (htsRequestIdRef.current !== requestId) {
                                return;
                              }

                              const hits =
                                res?.result?.result?.body?.hits?.hits || [];
                              const optionsMap = new Map();

                              hits.forEach((hit: any) => {
                                const src = hit._source;
                                if (
                                  src &&
                                  src['HS Code'] &&
                                  !isArchivedHtsRecord(src['HS Code'])
                                ) {
                                  const rawCode = String(src['HS Code'])
                                    .split(' - ')[0]
                                    .trim();
                                  const d = rawCode.replace(/\D/g, '');

                                  if (d.length >= 10) {
                                    const code10 = d.slice(0, 10);
                                    if (!optionsMap.has(code10)) {
                                      const pretty = formatHtsPretty(code10);
                                      const desc = src['Product Name'] || '';
                                      optionsMap.set(code10, {
                                        code: code10,
                                        label: `${pretty} - ${desc}`,
                                      });
                                    }
                                  }
                                }
                              });

                              // Merge, don't replace. The narrower fetch exists
                              // to surface codes the wider one's window cut
                              // off; discarding the wider results would just
                              // trade one gap for another, since a mistyped
                              // 10-digit code would then show no siblings to
                              // pick from at all.
                              setHtsOptions((prev) => {
                                const merged = new Map<string, any>(
                                  prev.map(
                                    (o: any) => [o.code, o] as [string, any],
                                  ),
                                );
                                optionsMap.forEach((opt, code10) => {
                                  merged.set(code10, opt);
                                });
                                return Array.from(merged.values()).sort(
                                  (a: any, b: any) =>
                                    a.code.localeCompare(b.code),
                                );
                              });
                            } catch (e) {
                              console.error(
                                'Error fetching HTS prefix options',
                                e,
                              );
                              // Let the next keystroke retry this prefix rather
                              // than leaving it permanently marked as fetched.
                              if (fetchedPrefixRef.current === prefixKey) {
                                fetchedPrefixRef.current = '';
                              }
                            } finally {
                              if (htsRequestIdRef.current === requestId) {
                                setIsHtsLoading(false);
                              }
                            }
                          }
                        } else {
                          resetHtsOptions();
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (readOnly) return;
                        if (typeof newValue === 'string') {
                          setInputs({
                            ...inputs,
                            htsCode: newValue.replace(/\D/g, ''),
                          });
                        } else if (newValue && newValue.code) {
                          setInputs({ ...inputs, htsCode: newValue.code });
                        } else {
                          setInputs({ ...inputs, htsCode: '' });
                        }
                      }}
                      sx={{
                        ...disabledSelectorSx,
                        ...tariffFieldSx,
                        width: '100%',
                        '& .MuiAutocomplete-clearIndicator': {
                          color: 'var(--focused-text-color)',
                          visibility: 'visible',
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Enter HTS Code'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {isHtsLoading ? (
                                  <CircularProgress color='inherit' size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                    {unsupportedHtsChapter && !readOnly && (
                      <div className='tariff-row-note tariff-row-note-warn'>
                        {unsupportedHtsChapterMessage(unsupportedHtsChapter)}
                      </div>
                    )}
                  </div>

                  {/* Origin Country */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>Origin Country</label>
                    <Autocomplete
                      options={countries as COpt[]}
                      getOptionLabel={(option) => displayLabel(option)}
                      isOptionEqualToValue={(opt, val) =>
                        (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                        getName(opt) === getName(val)
                      }
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      sx={{
                        ...disabledSelectorSx,
                        ...tariffFieldSx,
                        '::placeholder': { opacity: 1 },
                      }}
                      onChange={(_, newValue) =>
                        setInputs({
                          ...inputs,
                          originCountry: getName(newValue as COpt) || '',
                          originIso2: getIso(newValue as COpt) || '',
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Country of Origin'
                        />
                      )}
                      value={findCountryOption(
                        inputs.originIso2 || inputs.originCountry,
                      )}
                    />
                  </div>
                </div>

                {/* Product Value */}
                <div className='screening-input-minors'>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>Product Value</label>
                    <div
                      className='screening-input-field-container tariff-value-input'
                      style={{ position: 'relative', ...disabledSx }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--input-text-color)',
                          pointerEvents: 'none',
                        }}
                      >
                        $
                      </span>
                      <input
                        id='value'
                        type='text'
                        placeholder='Enter a Value of Products'
                        className='screening-input-field-content'
                        name='value'
                        value={String(productValue)}
                        onChange={(e) =>
                          setProductValue(parseCurrencyToNumber(e.target.value))
                        }
                        style={{ paddingLeft: 20 }}
                        readOnly={readOnly} // Use readOnly
                      />
                      {!readOnly && (
                        <svg
                          onClick={() => setProductValue(0)}
                          style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                        >
                          <path
                            d='M12 4L4 12M4 4L12 12'
                            stroke='var(--focused-text-color)'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Transportation Method */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Transportation Method</label>
                    <Autocomplete
                      // Remove disabled prop
                      options={['Ocean', 'Air', 'Land']}
                      getOptionLabel={(option) => option}
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      sx={{
                        ...disabledSx,
                        ...tariffFieldSx,
                      }}
                      value={inputs.transportMethod || 'Ocean'}
                      onChange={(_, newValue) => {
                        setInputs({
                          ...inputs,
                          transportMethod: newValue || 'Ocean',
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Transportation Method'
                        />
                      )}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Entry Date</label>
                    <DatePicker<Date>
                      // Remove disabled prop
                      value={effectDate}
                      onChange={(newValue: Date | null) => {
                        const ed = toDateOnly(newValue);
                        if (!ed) {
                          setEffectDate(null);
                          return;
                        }
                        const ld = toDateOnly(loadDate);
                        if (ld && ld.getTime() > ed.getTime()) {
                          setLoadDate(ed);
                        }
                        setEffectDate(ed);
                      }}
                      slots={{
                        actionBar: PickersActionBar,
                        textField: TextField,
                      }}
                      slotProps={{
                        actionBar: { actions: ['today'] },
                        textField: {
                          placeholder: 'Select Effective Date',
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            ...disabledSx,
                            ...tariffFieldSx,
                          },
                        },
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Loading Date</label>
                    <DatePicker<Date>
                      // Remove disabled prop
                      value={loadDate}
                      onChange={(newValue: Date | null) => {
                        const ld = newValue ? startOfDay(newValue) : null;
                        if (!ld) {
                          setLoadDate(null);
                          return;
                        }
                        const ed = effectDate ? startOfDay(effectDate) : null;
                        if (ed && ld.getTime() > ed.getTime()) {
                          setEffectDate(ld);
                        }
                        setLoadDate(ld);
                      }}
                      maxDate={effectDate ? startOfDay(effectDate) : undefined}
                      slots={{
                        actionBar: PickersActionBar,
                        textField: TextField,
                      }}
                      slotProps={{
                        actionBar: { actions: ['today'] },
                        textField: {
                          placeholder: 'Loading Date',
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            ...disabledSx,
                            ...tariffFieldSx,
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {(lineSplitShow || loadingCountryShow || ammountShow) && (
                  <div
                    className='screening-ai-label-text'
                    style={{ margin: '10px 0px' }}
                  >
                    Additional Information:
                  </div>
                )}

                <div className='screening-input-minors'>
                  {/* 🔽 Dynamic Loading Country */}
                  {loadingCountryShow && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '40%',
                        gap: '3px',
                      }}
                    >
                      <label>{loadingCountryFinalLabel}</label>
                      <Autocomplete
                        // Remove disabled prop
                        options={countries as COpt[]}
                        getOptionLabel={(option) => displayLabel(option)}
                        isOptionEqualToValue={(opt, val) =>
                          (Boolean(getIso(opt)) &&
                            getIso(opt) === getIso(val)) ||
                          getName(opt) === getName(val)
                        }
                        componentsProps={{
                          popper: {
                            sx: {
                              '& .MuiAutocomplete-option': {
                                color: 'var(--text-primary-white) !important',
                              },
                            }, // Higher z-index to sit above sidebar/modals
                          },
                          paper: {
                            sx: {
                              color: 'var(--text-primary-white) !important',
                            },
                          },
                        }}
                        sx={{
                          ...disabledSelectorSx,
                          ...tariffFieldSx,
                          '::placeholder': { opacity: 1 },
                        }}
                        onChange={(_, newValue) =>
                          setLoadingCountry(
                            getIso(newValue as COpt) ||
                              getName(newValue as COpt) ||
                              '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={`Select ${loadingCountryLabel}`}
                          />
                        )}
                        value={findCountryOption(loadingCountry)}
                      />
                    </div>
                  )}

                  {/* Material split(s) */}
                  {lineSplitShow && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '40%',
                        gap: '3px',
                      }}
                    >
                      {(tariffResults?.lineSplitFields?.length ?? 0) <= 1
                        ? (() => {
                            const firstKey =
                              (tariffResults?.lineSplitFields &&
                                tariffResults.lineSplitFields[0]?.key) ||
                              'SPLIT';
                            const labelText =
                              tariffResults?.lineSplitFields?.[0]?.label ??
                              tariffResults?.lineSplitLabel ??
                              'Material Percent';
                            const value = (materialPctByKey?.[firstKey] ??
                              (Number.isFinite(inputs.materialPct)
                                ? inputs.materialPct
                                : 0)) as number;
                            const orderKeys = (
                              tariffResults?.lineSplitFields ?? []
                            ).map((x: any) => x.key);

                            const syncDollarFromPct = (pct: number) => {
                              if (!productValue || productValue <= 0) {
                                setMaterialDollarInput('');
                                return;
                              }
                              const amt = (productValue * pct) / 100;
                              setMaterialDollarInput(amt.toFixed(2));
                            };

                            return (
                              <>
                                <label>
                                  {labelText.replace(
                                    /([a-z])([A-Z])/g,
                                    '$1 $2',
                                  )}
                                </label>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                  }}
                                >
                                  {/* Slider */}
                                  <Slider
                                    // Remove disabled prop
                                    sx={{ ...sliderSx, ...disabledSx }} // Apply disabledSx here too
                                    value={value}
                                    onChange={(_: any, v: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const n = clamp01pct(
                                        Array.isArray(v)
                                          ? Number(v[0])
                                          : Number(v),
                                      );
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                      syncDollarFromPct(n);
                                    }}
                                    valueLabelDisplay='off'
                                    step={0.01}
                                    min={0}
                                    max={100}
                                  />

                                  {/* % input */}
                                  <TextField
                                    // Remove disabled prop
                                    sx={{
                                      ...inputFieldSx,
                                      ...disabledSx,
                                      minWidth: 80,
                                      width: 'fit-content',
                                      maxWidth: 150,
                                      '& .MuiInputBase-root': { width: '100%' },
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                      },
                                    }}
                                    value={value}
                                    onChange={(e: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const n = clamp01pct(e.target.value);
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                      syncDollarFromPct(n);
                                    }}
                                    type='number'
                                    inputProps={{
                                      min: 0,
                                      max: 100,
                                      step: 0.01,
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment
                                          position='end'
                                          disableTypography
                                          sx={{
                                            color: 'var(--input-text-color)',
                                            fontFamily: 'var(--font-family)',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            margin: '0px',
                                          }}
                                        >
                                          %
                                        </InputAdornment>
                                      ),
                                    }}
                                  />

                                  {/* $ input */}
                                  <TextField
                                    // Remove disabled prop
                                    sx={{
                                      ...inputFieldSx,
                                      ...disabledSx,
                                      minWidth: 100,
                                      width: 'fit-content',
                                      maxWidth: 150,
                                      '& .MuiInputBase-root': { width: '100%' },
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                      },
                                    }}
                                    value={materialDollarInput}
                                    onChange={(e: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const raw = e.target.value;
                                      if (raw === '') {
                                        setMaterialDollarInput('');
                                        const n = 0;
                                        setInputs({
                                          ...inputs,
                                          materialPct: n,
                                        });
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            firstKey,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        return;
                                      }
                                      if (!/^\d*\.?\d{0,2}$/.test(raw)) {
                                        return;
                                      }
                                      setMaterialDollarInput(raw);
                                      const safeDollar =
                                        parseCurrencyToNumber(raw);
                                      if (!productValue || productValue <= 0) {
                                        const n = 0;
                                        setInputs({
                                          ...inputs,
                                          materialPct: n,
                                        });
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            firstKey,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        return;
                                      }
                                      const pctFromDollar =
                                        (safeDollar / productValue) * 100;
                                      const n = clamp01pct(pctFromDollar);
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                    }}
                                    // ADD THIS: Set ref to true on focus
                                    onFocus={() => {
                                      isDollarFocused.current = true;
                                    }}
                                    // UPDATE THIS: Set ref to false on blur
                                    onBlur={() => {
                                      isDollarFocused.current = false; // Mark as not focused

                                      // Existing blur logic...
                                      if (materialDollarInput === '') {
                                        return;
                                      }
                                      const safeDollar =
                                        parseCurrencyToNumber(
                                          materialDollarInput,
                                        );
                                      setMaterialDollarInput(
                                        Number.isFinite(safeDollar)
                                          ? safeDollar.toFixed(2)
                                          : '0.00',
                                      );
                                    }}
                                    type='text'
                                    inputProps={{ inputMode: 'decimal' }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment
                                          position='start'
                                          disableTypography
                                          sx={{
                                            color: 'var(--input-text-color)',
                                            fontFamily: 'var(--font-family)',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            margin: '0px',
                                          }}
                                        >
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Box>
                              </>
                            );
                          })()
                        : (tariffResults?.lineSplitFields ?? []).map(
                            (f: any, idx: number) => {
                              const orderKeys = (
                                tariffResults?.lineSplitFields ?? []
                              ).map((x: any) => x.key);
                              const value = (materialPctByKey?.[f.key] ??
                                0) as number;
                              const labelText =
                                f.label || 'Material Percentage';

                              // --- Added Sync Function ---
                              const syncMultiDollar = (
                                key: string,
                                pct: number,
                              ) => {
                                if (!productValue || productValue <= 0) {
                                  setMultiDollarInputs((prev) => ({
                                    ...prev,
                                    [key]: '',
                                  }));
                                  return;
                                }
                                const amt = (productValue * pct) / 100;
                                setMultiDollarInputs((prev) => ({
                                  ...prev,
                                  [key]: amt.toFixed(2),
                                }));
                              };

                              return (
                                <React.Fragment key={f.key}>
                                  <label>{labelText}</label>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 1,
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Slider
                                      sx={{ ...sliderSx, ...disabledSx }}
                                      value={value}
                                      onChange={(_: any, v: any) => {
                                        if (readOnly) {
                                          return;
                                        }
                                        const n = clamp01pct(
                                          Array.isArray(v)
                                            ? Number(v[0])
                                            : Number(v),
                                        );
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            f.key,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        if (idx === 0) {
                                          setInputs({
                                            ...inputs,
                                            materialPct: n,
                                          });
                                        }
                                        syncMultiDollar(f.key, n); // Bind sync
                                      }}
                                      valueLabelDisplay='off'
                                      step={0.01}
                                      min={0}
                                      max={100}
                                    />
                                    <TextField
                                      sx={{
                                        ...inputFieldSx,
                                        ...disabledSx,
                                        minWidth: 80,
                                      }}
                                      value={value}
                                      onChange={(e: any) => {
                                        if (readOnly) {
                                          return;
                                        }
                                        const n = clamp01pct(e.target.value);
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            f.key,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        if (idx === 0) {
                                          setInputs({
                                            ...inputs,
                                            materialPct: n,
                                          });
                                        }
                                        syncMultiDollar(f.key, n); // Bind sync
                                      }}
                                      type='number'
                                      inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: 0.01,
                                      }}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment
                                            position='end'
                                            disableTypography
                                            sx={{
                                              color: 'var(--input-text-color)',
                                              fontFamily: 'var(--font-family)',
                                              fontSize: '14px',
                                              fontWeight: 400,
                                              margin: '0px',
                                            }}
                                          >
                                            %
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                    {/* --- NEW DOLLAR INPUT --- */}
                                    <TextField
                                      sx={{
                                        ...inputFieldSx,
                                        ...disabledSx,
                                        minWidth: 100,
                                        width: 'fit-content',
                                        maxWidth: 150,
                                        '& .MuiInputBase-root': {
                                          width: '100%',
                                        },
                                        '& .MuiInputBase-input': {
                                          textAlign: 'right',
                                          overflow: 'visible',
                                          textOverflow: 'clip',
                                        },
                                      }}
                                      value={multiDollarInputs[f.key] ?? ''}
                                      onChange={(e: any) => {
                                        if (readOnly) return;
                                        const raw = e.target.value;
                                        if (raw === '') {
                                          setMultiDollarInputs((prev) => ({
                                            ...prev,
                                            [f.key]: '',
                                          }));
                                          const n = 0;
                                          setMaterialPctByKey((prev) =>
                                            boundedPctUpdate(
                                              prev ?? {},
                                              f.key,
                                              n,
                                              orderKeys,
                                            ),
                                          );
                                          if (idx === 0)
                                            setInputs({
                                              ...inputs,
                                              materialPct: n,
                                            });
                                          return;
                                        }
                                        if (!/^\d*\.?\d{0,2}$/.test(raw))
                                          return;
                                        setMultiDollarInputs((prev) => ({
                                          ...prev,
                                          [f.key]: raw,
                                        }));
                                        const safeDollar =
                                          parseCurrencyToNumber(raw);
                                        if (
                                          !productValue ||
                                          productValue <= 0
                                        ) {
                                          const n = 0;
                                          setMaterialPctByKey((prev) =>
                                            boundedPctUpdate(
                                              prev ?? {},
                                              f.key,
                                              n,
                                              orderKeys,
                                            ),
                                          );
                                          if (idx === 0)
                                            setInputs({
                                              ...inputs,
                                              materialPct: n,
                                            });
                                          return;
                                        }
                                        const pctFromDollar =
                                          (safeDollar / productValue) * 100;
                                        const n = clamp01pct(pctFromDollar);
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            f.key,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        if (idx === 0)
                                          setInputs({
                                            ...inputs,
                                            materialPct: n,
                                          });
                                      }}
                                      onFocus={() => {
                                        multiDollarFocus.current[f.key] = true;
                                      }}
                                      onBlur={() => {
                                        multiDollarFocus.current[f.key] = false;
                                        const currentVal =
                                          multiDollarInputs[f.key];
                                        if (
                                          currentVal === '' ||
                                          currentVal === undefined
                                        )
                                          return;
                                        const safeDollar =
                                          parseCurrencyToNumber(currentVal);
                                        setMultiDollarInputs((prev) => ({
                                          ...prev,
                                          [f.key]: Number.isFinite(safeDollar)
                                            ? safeDollar.toFixed(2)
                                            : '0.00',
                                        }));
                                      }}
                                      type='text'
                                      inputProps={{ inputMode: 'decimal' }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment
                                            position='start'
                                            disableTypography
                                            sx={{
                                              color: 'var(--input-text-color)',
                                              fontFamily: 'var(--font-family)',
                                              fontSize: '14px',
                                              fontWeight: 400,
                                              margin: '0px',
                                            }}
                                          >
                                            $
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Box>
                                </React.Fragment>
                              );
                            },
                          )}
                    </div>
                  )}

                  {/* Amount-based base duty */}
                  {ammountShow && (
                    <>
                      {Array.isArray(tariffResults?.requiredAmountUnits) &&
                      tariffResults.requiredAmountUnits.length > 0 ? (
                        tariffResults.requiredAmountUnits.map(
                          (unit: string) => (
                            <div
                              key={unit}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '20%',
                                gap: '3px',
                              }}
                            >
                              <label>
                                Ad Valorem -{' '}
                                {getEstimatedDutyDisplay(
                                  tariffResults.baseDutyRateRaw,
                                  unit,
                                  amountInputs[unit],
                                )}
                              </label>
                              <Box
                                sx={{ display: 'flex', gap: 1, height: '45px' }}
                              >
                                <TextField
                                  sx={{
                                    ...inputFieldSx,
                                    width: '100%',
                                    ...disabledSx,
                                  }}
                                  placeholder='0'
                                  type='number'
                                  value={
                                    Number.isFinite(Number(amountInputs[unit]))
                                      ? amountInputs[unit]
                                      : ''
                                  }
                                  onChange={(e: any) => {
                                    if (readOnly) {
                                      return;
                                    }
                                    const v =
                                      e.target.value === ''
                                        ? NaN
                                        : Number(e.target.value);
                                    setAmountInputs({
                                      ...amountInputs,
                                      [unit]: Number.isFinite(v) ? v : 0,
                                    });
                                  }}
                                  inputProps={{ min: 0 }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment
                                        position='end'
                                        disableTypography
                                        sx={{
                                          color: 'var(--input-text-color)',
                                          fontFamily: 'var(--font-family)',
                                          fontSize: '14px',
                                          fontWeight: 400,
                                        }}
                                      >
                                        {prettyUnit(unit)}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Box>
                            </div>
                          ),
                        )
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '20%',
                            gap: '3px',
                          }}
                        >
                          <label>Product Amount</label>
                          <Box sx={{ display: 'flex', gap: 1, height: '45px' }}>
                            <TextField
                              // Remove disabled prop
                              sx={{
                                ...inputFieldSx,
                                width: '60%',
                                ...disabledSx,
                              }}
                              placeholder='0'
                              type='number'
                              value={inputs.amountValue ?? ''}
                              onChange={(e: any) => {
                                if (readOnly) {
                                  return;
                                }
                                setInputs({
                                  ...inputs,
                                  amountValue:
                                    e.target.value === ''
                                      ? ''
                                      : Number(e.target.value),
                                });
                              }}
                              inputProps={{ min: 0 }}
                            />
                            <Select
                              // Remove disabled prop
                              sx={{
                                ...inputFieldSx,
                                width: '40%',
                                ...disabledSx,
                              }}
                              value={inputs.amountUnit || 'kg'}
                              onChange={(e: any) => {
                                if (readOnly) {
                                  return;
                                }
                                setInputs({
                                  ...inputs,
                                  amountUnit: e.target.value,
                                });
                              }}
                              displayEmpty
                            >
                              {metricUnits.map((u) => (
                                <MenuItem key={u} value={u}>
                                  {u}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally hide this entire button/disclaimer section if in ReadOnly Mode */}
      {!isBulkMode && (
        <div className='tariff-buttons-cont'>
          {!isWhiteLabel && showAnalyzerBanner && (
            <div className='tariff-analyzer-banner'>
              <div className='tariff-analyzer-banner-icon'>
                <QueryStatsOutlinedIcon fontSize='small' />
              </div>
              <div className='tariff-analyzer-banner-text'>
                <div className='tariff-analyzer-banner-title'>
                  Checking these one at a time?
                </div>
                <div className='tariff-analyzer-banner-subtitle'>
                  Run several HTS codes together in the Tariff Analyzer.
                </div>
              </div>
              <div className='tariff-analyzer-banner-actions'>
                <button
                  type='button'
                  className='btn-new-automate'
                  onClick={() => history.push('/tariff-analyzers')}
                >
                  <div style={{ display: 'flex', gap: '6px' }}>
                    Go to Tariff Analyzer
                    <ArrowForwardIcon style={{ fontSize: 16 }} />
                  </div>
                </button>
                <button
                  type='button'
                  className='tariff-analyzer-banner-dismiss'
                  aria-label='Dismiss'
                  onClick={() => setShowAnalyzerBanner(false)}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              </div>
            </div>
          )}
          <div className='disclaimer-text'>
            {disclaimerText}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div className='tariff-classify-link'>
              {/* <h3>Need your HTS Code? Use</h3>
              {isWhiteLabel && (
                <a
                  href='http://app.bitedata.io/'
                  target='_blank'
                  rel='noreferrer'
                >
                  <div className='screening-input-submit-group'>
                    <h4>BITE</h4>
                  </div>
                </a>
              )}
              {!isWhiteLabel && (
                <Link to='/bite-classify'>
                  <div className='screening-input-submit-group'>
                    <h4>Classify</h4>
                  </div>
                </Link>
              )} */}
            </div>

            <div className='tariff-buttons-group'>
              <div className='screening-input-submit-group'>
                <button
                  className={
                    isInteractionDisabled
                      ? 'btn-new-automate-disabled'
                      : 'btn-new-clear'
                  }
                  style={{ minWidth: '124px' }}
                  onClick={() => {
                    if (!isInteractionDisabled) {
                      clearPage();
                      setHtsInputValue('');
                    }
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {isInteractionDisabled && <LockIcon fontSize='small' />}
                    <ReplayIcon fontSize='small' />
                    Clear
                  </div>
                </button>
              </div>

              {isWhiteLabel && results && (
                <div className='tariff-buttons-group'>
                  <button
                    className={
                      isInteractionDisabled
                        ? 'btn-new-automate-disabled'
                        : 'btn-new-automate'
                    }
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        setShowDownload(true);
                      }
                    }}
                  >
                    <div>
                      {isInteractionDisabled && <LockIcon fontSize='small' />}
                      Download
                    </div>
                  </button>
                </div>
              )}

              {isWhiteLabel && !results && (
                <div className='screening-input-submit-group'>
                  <button className='btn-new-automate-disabled'>
                    <div>Download</div>
                  </button>
                </div>
              )}

              {!isWhiteLabel && results && (
                <div className='tariff-buttons-group'>
                  <button
                    className={
                      isInteractionDisabled
                        ? 'btn-new-automate-disabled'
                        : 'btn-new-automate'
                    }
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        setModalOpen(true);
                      }
                    }}
                  >
                    <div>
                      {isInteractionDisabled && <LockIcon fontSize='small' />}
                      Save
                    </div>
                  </button>
                </div>
              )}

              {!isWhiteLabel && !results && (
                <div className='screening-input-submit-group'>
                  <button className='btn-new-automate-disabled'>
                    <div>Save</div>
                  </button>
                </div>
              )}

              {inputs.htsCode.length > 0 &&
              inputs.originCountry.length > 0 &&
              !unsupportedHtsChapter ? (
                <div className='screening-input-submit-group'>
                  <button
                    className={
                      isInteractionDisabled
                        ? 'btn-new-automate-disabled'
                        : 'btn-new-automate'
                    }
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        handleSubmit();
                      }
                    }}
                  >
                    <div>
                      {isInteractionDisabled && <LockIcon fontSize='small' />}
                      Run Check
                    </div>
                  </button>
                </div>
              ) : (
                <div className='screening-input-submit-group'>
                  <button className='btn-new-automate-disabled'>
                    <div>Run Check</div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TariffRagSearch;
